import React from 'react'
import { useTranslation } from "react-i18next"
import { scaleBand, scaleLinear, scaleOrdinal, max as d3max } from 'd3'
import { useTheme, makeStyles } from '@material-ui/core/styles';
import D3Tooltip from './D3Tooltip';
import { formatLocaleNumber } from 'helpers/emeaHelpers';
import Axes from './Axes'
import ResponsiveWrapper from '../charts/ResponsiveWrapper'
import { Typography, Box } from '@material-ui/core'
import clsx from 'clsx';

const useStyles = makeStyles(theme => ({
  label: { color: theme.palette.menu.base },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  link: {
    cursor: 'pointer',
    textDecoration: 'underline',
    letterSpacing: '1px',
    padding: theme.spacing(.4),
    '&:hover': {
      color: theme.palette.link.hover
    }
  },
}));

const GroupedBarChart = ({
  data = [],
  keys = [],
  xKey = 'name',
  xLabel = null,
  height = 250,
  parentWidth = 600,
  margins = { top: 15, right: 20, bottom: 65, left: 40 },
  grouped = true,
  horizontal = false,
  setDetails,
  setDetailsData,
  id,
  showLegend = true,
  suffix = '',
  formatRequired = false,
  decimals = null,
  themeType = 'dark',
  barColors = 'set22',
  loading,
  showBarLabel = true,
  showXAxisLabel = true,
  rotateLabel = true,
  cursorType,
  ...rest
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const isLink = typeof (setDetails) === "function"

  const svgDimensions = { width: parentWidth, height: height / 2 + margins.top + margins.bottom };

  // Theme Colors
  const themeColors = theme.palette[barColors];
  // Bar Colors - either from props or theme
  let colors = scaleOrdinal().domain(keys).range(themeColors.filter(Boolean));

  // compute max value across all keys for grouped scale
  const maxValue = data.length && keys.length ? Math.max(...data.flatMap(d => keys.map(k => +d[k] || 0))) : 0;

  // primary scales
  let xScale = scaleBand()
    .domain(data.map(d => d[xKey]))
    .range([margins.left, svgDimensions.width - margins.right])
    .padding(0.2);
  let yScale = scaleLinear()
    .domain([0, maxValue])
    .nice()
    .range([svgDimensions.height - margins.bottom, margins.top]);

  // inner scale for groups
  let innerScale = null;
  if (grouped && keys.length > 0 && !horizontal) {
    innerScale = scaleBand()
      .domain(keys)
      .range([0, xScale.bandwidth()])
      .padding(0.1);
  }

  // horizontal variant
  if (horizontal) {
    yScale = scaleBand()
      .domain(data.map(d => d[xKey]))
      .range([margins.top, svgDimensions.height - margins.bottom])
      .padding(0.2);
    xScale = scaleLinear()
      .domain([0, maxValue])
      .nice()
      .range([margins.left, svgDimensions.width - margins.right]);
    if (grouped && keys.length > 0) {
      innerScale = scaleBand()
        .domain(keys)
        .range([0, yScale.bandwidth()])
        .padding(0.1);
    }
  }

  const NoDataFound = () => {
    const text = loading ? "loading" : "no_data_found"
    return (
      <g transform={`translate(${svgDimensions.width / 2}, ${svgDimensions.height / 2})`} data-testid={id}>
        <text textAnchor="middle" fill="white">{t(text)}</text>
      </g>
    )
  }

  const renderBars = () => {
    if (!grouped) return null;

    if (!horizontal) {
      // vertical grouped bars
      return data.flatMap(d => keys.map(k => {
        const x = xScale(d[xKey]) + (innerScale ? innerScale(k) : 0);
        const bw = innerScale ? innerScale.bandwidth() : xScale.bandwidth();
        const value = +d[k] || 0;
        const y = yScale(value);
        const h = yScale(0) - y;

        const handleClick = () => {
          if (isLink) {
            if (setDetailsData) setDetails({ ...d, key: k });
          }
        }

        return (
          <D3Tooltip
            placement={horizontal ? 'top' : 'right'}
            interactive
            themeType={themeType}
            color={colors(k)}
            title={
              <div data-testid={`tooltip-${d[xKey]}-${k}`} role='button' className={clsx(isLink ? classes.link : {})} onClick={handleClick}>
                {d[xKey]} : {formatLocaleNumber(value, formatRequired, decimals)}{suffix}
              </div>
            }
          >
            <g>
            <rect
              key={`${d[xKey]}-${k}`}
              x={x}
              y={y}
              width={bw}
              height={h}
              fill={colors(k)}
              data-testid={`bar-${d[xKey]}-${k}`}
            // onMouseEnter={() => setDetails && setDetails(d, k)}
            />
              {showBarLabel && (
                <text
                  fontSize={12}
                  textAnchor="middle"
                  alignmentBaseline="middle"
                  fill={themeType === "dark" ? "white" : "black"}
                  x={x + bw / 2}
                  y={y - 5}
                >
                  {formatLocaleNumber(value, formatRequired, decimals)}{suffix}
                </text>
              )}
            </g>
          </D3Tooltip>
        )
      }))
    }

    // horizontal grouped bars
    return data.flatMap(d => keys.map(k => {
      const y = yScale(d[xKey]) + (innerScale ? innerScale(k) : 0);
      const bh = innerScale ? innerScale.bandwidth() : yScale.bandwidth();
      const value = +d[k] || 0;
      const x0 = xScale(0);
      const bx = xScale(value);
      const bw = bx - xScale(0);
      return (
        <g key={`${d[xKey]}-${k}`}>
        <rect
          key={`${d[xKey]}-${k}`}
          x={x0}
          y={y}
          width={bw}
          height={bh}
          fill={colors(k)}
          data-testid={`bar-${d[xKey]}-${k}`}
        // onMouseEnter={() => setDetails && setDetails(d, k)}
        />

          {showBarLabel && (
            <text
              fontSize={12}
              textAnchor="middle"
              alignmentBaseline="middle"
              fill={themeType === "dark" ? "white" : "black"}
            >
              {formatLocaleNumber(value, formatRequired, decimals)}{suffix}
            </text>
          )}
        </g>
      )
    }))
  }

  return (
    <span data-testid="grouped-bar-chart" >

      <svg width={svgDimensions.width} height={svgDimensions.height}>
        {!data.length || maxValue === 0 || loading ? <NoDataFound /> : (
          <g>
            {renderBars()}
            <Axes
              horizontal={horizontal}
              wrapit={true}
              rotateLabel={rotateLabel}
              scales={{ xScale, yScale }}
              margins={margins}
              svgDimensions={svgDimensions}
              xLabel={xLabel || xKey}
              showEllipsis={true}
              showXAxisLabel={showXAxisLabel}
              cursorType={cursorType}
            />
          </g>
        )}
      </svg>

      {showLegend && keys.length && data.length && !loading ? (
        <Box display="flex" gridGap={8} ml={6}>
          {keys.map(k => (
            <Box key={k} className={classes.legendItem}>
              <svg width={12} height={15}><rect width={12} height={15} fill={colors(k)} /></svg>
              <Typography variant="caption">{data?.[0]?.[rest.legendKeyMap?.[k]] || t(k)}</Typography>
            </Box>
          ))}
        </Box>
      ) : null}
    </span>
  )
}

export default ResponsiveWrapper(GroupedBarChart)
